export interface PunchLocationPayload {
    latitude: number;
    longitude: number;
    accuracyMeters: number;
}

// Kept short deliberately: this gates how long the punch button appears to "hang"
// waiting on a GPS fix. 5s is enough for a warm/cached fix on most phones without
// making an employee stare at a spinner. Geolocation never blocks the punch either
// way — it just determines whether we have coordinates to send with it.
const GEOLOCATION_TIMEOUT_MS = 5_000;

// Turns a GeolocationPosition into our payload, or undefined if it's not
// trustworthy — missing `coords`, a missing/non-numeric field, or a `coords`
// getter that itself throws are all degraded the same way: no payload, never
// a thrown error. This runs entirely inside the caller's try/catch.
const toPayload = (position: GeolocationPosition): PunchLocationPayload | undefined => {
    const coords = position?.coords;
    if (
        !coords ||
        typeof coords.latitude !== 'number' ||
        typeof coords.longitude !== 'number' ||
        typeof coords.accuracy !== 'number'
    ) {
        return undefined;
    }
    return {
        latitude: coords.latitude,
        longitude: coords.longitude,
        accuracyMeters: coords.accuracy,
    };
};

// The browser's own `timeout` option is a *request*, not a guarantee — some
// devices/browsers are known to never invoke either callback at all. This guard
// timer is what actually promises the Promise settles: it always resolves
// (never rejects) once, no matter what the browser API does or doesn't do.
export const readPunchLocation = (): Promise<PunchLocationPayload | undefined> =>
    new Promise((resolve) => {
        let settled = false;
        const settle = (value: PunchLocationPayload | undefined) => {
            if (settled) {
                return;
            }
            settled = true;
            resolve(value);
        };

        // Inside the try, not before it. `navigator.geolocation` is a property access, and
        // a throwing getter (a hardened/instrumented browser, an over-eager privacy
        // extension) would reject this promise — which clockIn/clockOut await unguarded,
        // so the punch itself would fail. The contract here is "never rejects", and that
        // has to cover the feature-detection too.
        const guardTimer = setTimeout(() => settle(undefined), GEOLOCATION_TIMEOUT_MS + 1_000);

        try {
            if (typeof navigator === 'undefined' || !navigator.geolocation) {
                clearTimeout(guardTimer);
                settle(undefined);
                return;
            }

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    // Invariant: nothing between disarming the guard and calling
                    // settle() may throw. Reading/validating the position happens
                    // first, inside this try/catch, and only then do we disarm the
                    // guard and settle — so a malformed position (missing `coords`,
                    // a non-numeric field, or a `coords` getter that throws) still
                    // resolves undefined instead of leaving the promise hanging.
                    let payload: PunchLocationPayload | undefined;
                    try {
                        payload = toPayload(position);
                    } catch {
                        payload = undefined;
                    }
                    clearTimeout(guardTimer);
                    settle(payload);
                },
                () => {
                    clearTimeout(guardTimer);
                    settle(undefined);
                },
                {
                    enableHighAccuracy: true,
                    timeout: GEOLOCATION_TIMEOUT_MS,
                    maximumAge: 0,
                },
            );
        } catch {
            clearTimeout(guardTimer);
            settle(undefined);
        }
    });
